import { getChartingResource } from '@/features/charting-next/api/chartingClient';
import { normalizeChartingStock } from '@/features/charting-next/adapters/chartingCollectionAdapters';
import { normalizeChartingOhlcSeries } from '@/features/charting-next/adapters/chartingInsightsAdapters';
import { ForumError, staleResponse } from './forumErrors';
import { forumId } from '../models/forumMappers';
import { forumTicker } from '../models/forumStock';

const incomplete = () => new ForumError('stock_invalid', 'No se pudieron interpretar los datos del activo. Puedes volver a intentarlo.');
const numeric = value => (typeof value === 'number' || typeof value === 'string' && value.trim() !== '') && Number.isFinite(Number(value));

// No Vuex access, global stock selection, shared mutable cache or writes. The
// caller owns this repository and its lifetime; a new session invalidates reads.
export function createForumStockRepository({ get = getChartingResource, getIdentity, now = Date.now }) {
  async function read(ticker, loader) {
    const identity = getIdentity();
    if (!forumId(identity?.uid)) throw new ForumError('authentication_required', 'Inicia sesión para consultar el activo.', 401);
    const symbol = forumTicker(ticker), owner = JSON.stringify(identity);
    if (!symbol) throw incomplete();
    const current = () => { if (JSON.stringify(getIdentity()) !== owner) throw staleResponse(); };
    const request = async (path, options) => { const value = await get(path, options); current(); return value; };
    try { const result = await loader(symbol, request); current(); return result; }
    catch (error) {
      current();
      if (error instanceof ForumError) throw error;
      const status = error?.status || error?.response?.status || null;
      throw new ForumError('stock_unavailable', [401, 403].includes(status) ? 'No tienes acceso a estos datos del activo.' : 'No se pudieron cargar los datos del activo. Puedes volver a intentarlo.', status);
    }
  }
  return {
    stock: ticker => read(ticker, async (symbol, request) => {
      let rows = await request('/api/v1/stock-finder/stocks/symbol/' + encodeURIComponent(symbol));
      if (!Array.isArray(rows)) throw incomplete();
      if (!rows.length) {
        const fallback = await request('/api/v1/stock-finder/stocks/symbol-without-entry/' + encodeURIComponent(symbol));
        rows = fallback ? [fallback] : [];
      }
      if (rows.some(row => !row || forumTicker(row.ticker || row.symbol) !== symbol)) throw incomplete();
      return rows.map(normalizeChartingStock);
    }),
    chart: ticker => read(ticker, async (symbol, request) => {
      const first = await request('/stockchart/api/history/firstohlc', { params: { symbol } });
      const to = Math.floor(now() / 1000);
      if (first !== false && !numeric(first) || Number(first) > to) throw incomplete();
      // strtotime returns false for missing history (0 is also used by callers).
      // Request from epoch in that case,
      // without asserting ATH coverage for instruments starting before 1970.
      const hasFullHistory = numeric(first) && Number(first) !== 0 && Number(first) <= to;
      const payload = await request('/stockchart/api/history', { params: { symbol, resolution: 'M', from: hasFullHistory ? Number(first) : 0, to } });
      if (payload?.s === 'no_data') return { candles: [], volumes: [], hasFullHistory };
      if (!payload || payload.s !== 'ok' || !['t', 'o', 'h', 'l', 'c'].every(key => Array.isArray(payload[key]) && payload[key].length === payload.t.length)) throw incomplete();
      // The legacy formatter serializes timestamp 0 (January 1970) as ''. Do
      // not invent a date for it or reject the rest of an otherwise valid series.
      // Its established mapper omits undated bars; expose that loss explicitly.
      let previousTime = -Infinity;
      if (payload.t.some((time, index) => {
        if (time === '') return false;
        const invalid = !['t', 'o', 'h', 'l', 'c'].every(key => numeric(payload[key][index]))
        || Number(time) <= previousTime
        || Number(payload.h[index]) < Math.max(Number(payload.o[index]), Number(payload.c[index]), Number(payload.l[index]))
        || Number(payload.l[index]) > Math.min(Number(payload.o[index]), Number(payload.c[index]));
        previousTime = Number(time); return invalid;
      })) throw incomplete();
      const omittedBars = payload.t.filter(time => time === '').length;
      const series = normalizeChartingOhlcSeries(payload);
      if (payload.t.length && !series.candles.length) throw incomplete();
      return { ...series, omittedBars, hasFullHistory: hasFullHistory && !omittedBars };
    })
  };
}
